import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';
import { canAssignClients } from '../../lib/roles';
import { useFeatures } from '../../lib/features';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Plus, Upload, Hand, Loader2, UserCheck, Trash2, Edit } from 'lucide-react';
import {
  ClientFilters, useClientSearch, Pager, StatusPill, EmptyMsg, showDay, apiError,
} from '../sales/salesShared';
import ActivityModal from '../sales/ActivityModal';
import ImportClientsModal from './ImportClientsModal';

// The client list for large properties: a table whose search, filters and
// paging run on the server. Admins can tick rows (or everything matching the
// filters) and assign them to a rep in one go; reps can claim unassigned ones.
const ClientsDirectory = ({ salesReps, onNew, onEdit, refreshKey = 0 }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { clientsLabel, clientLabel } = useFeatures();
  const isSalesRep = user?.role === 'sales_rep';
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  // Admins, accounting and sales admins give clients to reps; everyone sees who has what.
  const canAssign = canAssignClients(user);
  const myRepId = user?.matched_sales_rep_id || null;

  const [filters, setFilters] = useState({ q: '', rep: 'all', industry: 'all', status: 'all', postal: '', has_phone: false });
  const search = useClientSearch(filters, { pageSize: 50, sort: 'name' });
  const [facets, setFacets] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [assignTo, setAssignTo] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [logFor, setLogFor] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [claiming, setClaiming] = useState(null);

  const loadFacets = useCallback(async () => {
    try { setFacets(await api.get('/advertisers/facets')); } catch (_) { /* filters still work without counts */ }
  }, []);
  useEffect(() => { loadFacets(); }, [loadFacets]);

  const { reload } = search;
  const refresh = useCallback(async () => { await Promise.all([reload(), loadFacets()]); }, [reload, loadFacets]);
  // The page that owns the create/edit dialog bumps refreshKey after a save.
  useEffect(() => { if (refreshKey) refresh(); }, [refreshKey, refresh]);

  // A new filter is a new list: forget the old selection.
  const filterKey = JSON.stringify(search.appliedFilters);
  useEffect(() => { setSelected(new Set()); setAllMatching(false); }, [filterKey]);

  const repName = (id) => salesReps.find(r => r.id === id)?.name;
  const pageIds = search.items.map(c => c.id);
  const pageAllSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));
  const selectedCount = allMatching ? search.total : selected.size;

  const toggle = (id) => {
    setAllMatching(false);
    setSelected(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };
  const togglePage = () => {
    setAllMatching(false);
    setSelected(prev => {
      const next = new Set(prev);
      if (pageAllSelected) pageIds.forEach(id => next.delete(id)); else pageIds.forEach(id => next.add(id));
      return next;
    });
  };

  const bulkAssign = async () => {
    if (!assignTo || !selectedCount) return;
    const target = assignTo === '__unassign' ? null : assignTo;
    const who = target ? repName(target) : 'nobody (unassigned)';
    if (!window.confirm(`Assign ${selectedCount.toLocaleString()} ${clientsLabel.toLowerCase()} to ${who}?`)) return;
    setAssigning(true);
    setError('');
    setMessage('');
    try {
      const body = allMatching
        ? { sales_rep_id: target, filter: search.appliedFilters }
        : { sales_rep_id: target, ids: Array.from(selected) };
      const res = await api.post('/advertisers/bulk-assign', body);
      setMessage(`${res.modified.toLocaleString()} ${clientsLabel.toLowerCase()} now assigned to ${who}.`);
      setSelected(new Set());
      setAllMatching(false);
      setAssignTo('');
      await refresh();
    } catch (e) {
      setError(apiError(e, 'Could not assign'));
    } finally {
      setAssigning(false);
    }
  };

  const claim = async (c) => {
    setClaiming(c.id);
    setError('');
    try {
      await api.post(`/advertisers/${c.id}/claim`, {});
      await refresh();
    } catch (e) {
      setError(apiError(e, 'Could not claim'));
    } finally {
      setClaiming(null);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.business_name}"? Its activity history stays in the log.`)) return;
    try {
      await api.delete(`/advertisers/${c.id}`);
      await refresh();
    } catch (e) {
      setError(apiError(e, 'Could not delete'));
    }
  };

  const canWrite = (c) => !isSalesRep || !c.assigned_sales_rep_id || c.assigned_sales_rep_id === myRepId;
  // Everything can only be selected when at least one filter narrows the list.
  const hasFilter = Object.entries(search.appliedFilters).some(([, v]) => v && v !== 'all');

  return (
    <div className="space-y-4" data-testid="clients-directory">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#F8FAFC] tracking-tight">{clientsLabel}</h1>
          <p className="text-[#94A3B8] mt-1" data-testid="clients-total">
            {facets
              ? `${facets.total.toLocaleString()} ${clientsLabel.toLowerCase()}, ${facets.unassigned.toLocaleString()} unassigned`
              : 'Loading…'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => setShowImport(true)} className="border-[#1E293B] bg-[#121212] text-[#94A3B8] hover:text-[#F8FAFC]" data-testid="import-clients-btn">
              <Upload className="w-4 h-4 mr-2" /> Import
            </Button>
          )}
          <Button onClick={onNew} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="create-advertiser-btn">
            <Plus className="w-4 h-4 mr-2" /> New {clientLabel}
          </Button>
        </div>
      </div>

      <ClientFilters filters={filters} onChange={setFilters} facets={facets} salesReps={salesReps} isSalesRep={isSalesRep} everyRep />

      {canAssign && selectedCount > 0 && (
        <div className="flex flex-wrap items-center gap-3 bg-[#1E293B]/50 border border-[#3B82F6]/40 rounded-lg p-3" data-testid="bulk-bar">
          <span className="text-sm text-[#F8FAFC]" data-testid="bulk-count">{selectedCount.toLocaleString()} selected</span>
          {!allMatching && hasFilter && search.total > selected.size && (
            <button type="button" onClick={() => setAllMatching(true)} className="text-sm text-[#3B82F6]" data-testid="select-all-matching">
              Select all {search.total.toLocaleString()} that match these filters
            </button>
          )}
          {allMatching && <span className="text-xs text-amber-300">Everything matching the filters, on every page.</span>}
          <div className="flex items-center gap-2 ml-auto">
            <Select value={assignTo} onValueChange={setAssignTo}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9 w-[200px]" data-testid="bulk-rep-select"><SelectValue placeholder="Assign to…" /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#1E293B]">
                {salesReps.map(r => <SelectItem key={r.id} value={r.id} className="text-[#F8FAFC]">{r.name}</SelectItem>)}
                <SelectItem value="__unassign" className="text-[#94A3B8]">Nobody (unassign)</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={bulkAssign} disabled={!assignTo || assigning} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white h-9" data-testid="bulk-assign-btn">
              {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserCheck className="w-4 h-4 mr-2" /> Assign</>}
            </Button>
            <Button variant="ghost" onClick={() => { setSelected(new Set()); setAllMatching(false); }} className="text-[#94A3B8] h-9">Clear</Button>
          </div>
        </div>
      )}

      {message && <div className="text-sm text-emerald-400" data-testid="clients-message">{message}</div>}
      {(error || search.error) && <div className="text-sm text-red-400" data-testid="clients-error">{error || search.error}</div>}

      <div className="bg-[#121212] border border-[#1E293B] rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="clients-table">
          <thead className="bg-[#0A0A0A] text-[#94A3B8] text-xs uppercase">
            <tr>
              {canAssign && (
                <th className="px-3 py-2 w-8">
                  <input type="checkbox" checked={pageAllSelected} onChange={togglePage} className="accent-[#3B82F6]" title="Select this page" data-testid="select-page" />
                </th>
              )}
              <th className="text-left px-3 py-2">{clientLabel}</th>
              <th className="text-left px-3 py-2">Phone / Email</th>
              <th className="text-left px-3 py-2">Sales rep</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Last contact</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {search.items.map(c => (
              <tr key={c.id} className="border-t border-[#1E293B] hover:bg-[#0A0A0A]/60" data-testid={`client-row-${c.id}`}>
                {canAssign && (
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={allMatching || selected.has(c.id)} onChange={() => toggle(c.id)} className="accent-[#3B82F6]" data-testid={`select-${c.id}`} />
                  </td>
                )}
                <td className="px-3 py-2">
                  <button type="button" onClick={() => navigate(`/advertisers/${c.id}`)} className="text-left text-[#F8FAFC] hover:text-[#3B82F6]" data-testid={`client-open-${c.id}`}>
                    {c.business_name}
                  </button>
                  {c.created_by === 'newsdesk' && (
                    <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide bg-[#3B82F6]/15 text-[#3B82F6]" title="Created from an email in Newsdesk" data-testid={`client-newsdesk-badge-${c.id}`}>Newsdesk</span>
                  )}
                  <div className="text-xs text-[#475569] truncate max-w-[340px]">{[c.industry, c.address].filter(Boolean).join(' · ')}</div>
                </td>
                <td className="px-3 py-2 text-[#94A3B8]">
                  <div className="whitespace-nowrap">{c.phone || <span className="text-[#475569]">—</span>}</div>
                  {c.email && <div className="text-xs truncate max-w-[220px]">{c.email}</div>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {c.assigned_sales_rep_id
                    ? <span className="text-[#F8FAFC]">{repName(c.assigned_sales_rep_id) || '—'}</span>
                    : <span className="text-amber-400 text-xs">Unassigned</span>}
                </td>
                <td className="px-3 py-2"><StatusPill status={c.sales_status} /></td>
                <td className="px-3 py-2 text-[#94A3B8] whitespace-nowrap">{c.last_contact_date ? showDay(c.last_contact_date) : 'never'}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-1">
                    {isSalesRep && !c.assigned_sales_rep_id && (
                      <Button size="sm" variant="outline" onClick={() => claim(c)} disabled={claiming === c.id} className="border-[#1E293B] bg-[#121212] text-[#F8FAFC] h-7 text-xs" data-testid={`claim-${c.id}`}>
                        <Hand className="w-3 h-3 mr-1" /> {claiming === c.id ? '…' : 'Claim'}
                      </Button>
                    )}
                    {canWrite(c) && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setLogFor(c)} className="text-[#3B82F6] h-7 px-2" title="Log activity" data-testid={`client-log-${c.id}`}><Plus className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => onEdit(c)} className="text-[#94A3B8] h-7 px-2" title="Edit" data-testid={`client-edit-${c.id}`}><Edit className="w-4 h-4" /></Button>
                      </>
                    )}
                    {isAdmin && (
                      <Button size="sm" variant="ghost" onClick={() => remove(c)} className="text-red-400 h-7 px-2" title="Delete" data-testid={`client-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {search.loading && search.items.length === 0 && <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#3B82F6]" /></div>}
        {!search.loading && search.items.length === 0 && (
          <EmptyMsg text={facets && facets.total === 0 ? `No ${clientsLabel.toLowerCase()} yet. Use Import to load a spreadsheet.` : 'Nothing matches these filters.'} />
        )}
      </div>

      <Pager page={search.page} pages={search.pages} total={search.total} pageSize={search.pageSize} onPage={search.setPage} testid="clients-pager" />

      <ActivityModal open={!!logFor} client={logFor} onClose={() => setLogFor(null)} onSaved={refresh} />
      <ImportClientsModal open={showImport} onClose={() => setShowImport(false)} onImported={refresh} clientsLabel={clientsLabel} />
    </div>
  );
};

export default ClientsDirectory;
